import { useState, useEffect } from "react";
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AssistantForm } from "./AssistantForm";
import { useAssistantUpdate } from "./useAssistantUpdate";
import { useAssistantForm } from "./hooks/useAssistantForm";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";

interface EditAssistantDialogProps {
  assistantId: string;
  onSave: () => void;
}

interface Assistant {
  id: string;
  name: string;
  description: string;
  model: string;
  creativity: string;
  vector_store_id: string | null;
}

const EditAssistantDialog = ({ assistantId, onSave }: EditAssistantDialogProps) => {
  const [assistant, setAssistant] = useState<Assistant | null>(null);
  const { isLoading, isEnhancing, updateAssistant } = useAssistantUpdate(onSave);
  const {
    instructions,
    setInstructions,
    handleEnhanceInstructions
  } = useAssistantForm(onSave, assistant?.description || "");

  useEffect(() => {
    const fetchAssistant = async () => {
      console.log('Fetching assistant details for ID:', assistantId);
      
      const { data, error } = await supabase
        .from('assistants')
        .select('*')
        .eq('id', assistantId)
        .single();

      if (error) {
        console.error('Error fetching assistant:', error);
        toast.error("Failed to fetch assistant details");
        return;
      }

      console.log('Fetched assistant data:', data);
      setAssistant(data);
      setInstructions(data.description || "");
    };

    fetchAssistant();
  }, [assistantId]);

  if (!assistant) {
    return (
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Loading...</DialogTitle>
        </DialogHeader>
      </DialogContent>
    );
  }

  return (
    <DialogContent className="sm:max-w-[600px]">
      <DialogHeader>
        <DialogTitle>Edit Assistant</DialogTitle>
      </DialogHeader>
      <AssistantForm
        onSubmit={(data) => updateAssistant(assistantId, data)}
        isLoading={isLoading}
        isEnhancing={isEnhancing}
        instructions={instructions}
        onInstructionsChange={setInstructions}
        onEnhanceInstructions={handleEnhanceInstructions}
        initialValues={{
          name: assistant.name,
          model: assistant.model || 'gpt-4o-mini',
          creativity: assistant.creativity || 'neutral',
          assistantId: assistant.id
        }}
      />
    </DialogContent>
  );
};

export default EditAssistantDialog;